// ABOUTME: Presents the current asynchronous cancellation request status on order details.
// ABOUTME: Offers refresh after completion and dismissal after rejection.
import {Alert, Button} from '@hm/fundamental'
import {mdiClose, mdiRefresh} from '@mdi/js'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {CancellationBannerVariant} from '../../types/cancellationBanner.types'

interface OrderCancellationBannerProps {
  variant: CancellationBannerVariant
  onRefresh: () => void
  onDismiss: () => void
  isRefreshing?: boolean
  isDismissing?: boolean
}

const ALERT_VARIANTS: Record<CancellationBannerVariant, 'info' | 'error'> = {
  cancellationProcessing: 'info',
  cancellationCompleted: 'info',
  cancellationFailed: 'error'
}

export const OrderCancellationBanner: React.FC<OrderCancellationBannerProps> = ({
  variant,
  onRefresh,
  onDismiss,
  isRefreshing = false,
  isDismissing = false
}) => {
  const {t} = useTranslation()
  const title =
    variant === 'cancellationCompleted'
      ? t('orderIntake.cancellationBanner.completedTitle', 'Order cancellation completed')
      : variant === 'cancellationFailed'
        ? t('orderIntake.cancellationBanner.failedTitle', 'Order cancellation was not approved')
        : undefined
  const description =
    variant === 'cancellationProcessing'
      ? t(
          'orderIntake.cancellationBanner.processingDescription',
          'This order cancellation is being processed. Please check back later.'
        )
      : variant === 'cancellationCompleted'
        ? t(
            'orderIntake.cancellationBanner.completedDescription',
            'The order cancellation is complete. It may take a few minutes for the order status to update to Cancelled. Please reload this order shortly.'
          )
        : t(
            'orderIntake.cancellationBanner.failedDescription',
            'Please contact dispatch if you need help with this cancellation.'
          )

  return (
    <Alert
      variant={ALERT_VARIANTS[variant]}
      className="[&>*:last-child]:flex-1"
      data-test-id={`order-cancellation-banner-${variant}`}
    >
      <div className="flex w-full items-start justify-between gap-4" aria-live="polite">
        <div className="flex flex-col gap-1">
          {title && <p className="text-sm font-semibold">{title}</p>}
          <p className="text-sm">{description}</p>
        </div>
        {variant === 'cancellationCompleted' && (
          <Button variant="tertiary" icon={mdiRefresh} onClick={onRefresh} disabled={isRefreshing}>
            {t('reload', 'Refresh')}
          </Button>
        )}
        {variant === 'cancellationFailed' && (
          <Button variant="tertiary" icon={mdiClose} onClick={onDismiss} disabled={isDismissing}>
            {t('dismiss', 'Dismiss')}
          </Button>
        )}
      </div>
    </Alert>
  )
}
